import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  User, 
  Truck, 
  Wrench, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Trash2, 
  ZoomIn, 
  ZoomOut,
  Play,
  Check,
  RefreshCw
} from 'lucide-react';
import moment from 'moment';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import EditAssignmentModal from './EditAssignmentModal'; 
import AddResourceModal from './AddResourceModal';
import NotificationContainer from './NotificationContainer';
import ResourceInfoPopup from './ResourceInfoPopup';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const projectColors = {
  'Project A': '#3498db',
  'Project B': '#2ecc71',
  'Project C': '#e74c3c',
  'Project D': '#f39c12',
  'Travel': '#95a5a6',
  'Time Off': '#34495e'
};

const AIAssistant = () => {
  // ============================
  // 1. State Declarations
  // ============================
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]); 
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [setSelectedDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(moment().startOf('week'));
  const [viewMode, setViewMode] = useState('2 Weeks');
  const [filter, setFilter] = useState({ type: 'All', project: 'All' });
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [filteredResources, setFilteredResources] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsVisible, setIsNotificationsVisible] = useState(true);
  const [splitActivityMode, setSplitActivityMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredResource, setHoveredResource] = useState(null);
  const [setLoadingProjects] = useState(true);
  const [setErrorLoadingProjects] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [setAiModel] = useState(null);
  const [isAiLearning, setIsAiLearning] = useState(false);
  const [aiObservations, setAiObservations] = useState([]);

  const containerRef = useRef(null);

  // ============================
  // 2. Utility Functions
  // ============================

  const addNotification = useCallback((message) => {
    const newNotification = { id: Date.now(), message };
    setNotifications(prev => [...prev, newNotification]);
    setTimeout(() => removeNotification(newNotification.id), 5000);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const toggleNotificationsVisibility = useCallback(() => {
    setIsNotificationsVisible(prev => !prev);
  }, []);

  // ============================
  // 3. Derived Values
  // ============================

  const daysToShow = useMemo(() => {
    switch (viewMode) {
      case 'Week': return 7;
      case '2 Weeks': return 14;
      case 'Month': return 30;
      case '3 Months': return 90;
      case '6 Months': return 180;
      case 'Year': return 365;
      default: return 14;
    }
  }, [viewMode]);

  // ============================
  // 4. Helper Functions
  // ============================

  const getResourceIcon = useCallback((type) => {
    if (type === 'Person') return <User size={16} className="mr-2" />;
    if (type === 'Vehicle') return <Truck size={16} className="mr-2" />;
    if (type === 'Machine') return <Wrench size={16} className="mr-2" />;
    return null;
  }, []);

  const renderResourceName = useCallback((resource) => {
    if (resource.type === 'Machine') return resource.machine_type;
    if (resource.type === 'Person') return `${resource.first_name} ${resource.last_name}`;
    if (resource.type === 'Vehicle') return resource.car_brand;
    return resource.name;
  }, []);

  const calculateWorkDays = useCallback((resourceId) => {
    return assignments
      .filter(a => a.resourceId === resourceId && a.projectId !== 'Time Off' && a.projectId !== 'Travel')
      .reduce((total, a) => {
        const start = moment(a.start);
        const end = moment(a.end);
        return total + end.diff(start, 'days') + 1;
      }, 0);
  }, [assignments]);

  // ============================
  // 5. AI Simulation and Learning Functions
  // ============================

  const calculateResourceUtilization = useCallback(() => {
    return resources.map(resource => ({
      id: resource.id,
      name: renderResourceName(resource),
      utilization: (calculateWorkDays(resource.id) / daysToShow) * 100
    }));
  }, [resources, daysToShow, renderResourceName, calculateWorkDays]);

  const calculateProjectProgress = useCallback(() => {
    return projects.map(project => ({
      id: project.id,
      name: project.name,
      progress: Math.random() * 100 // Example random value, replace with actual calculation
    }));
  }, [projects]);

  const identifyPotentialConflicts = useCallback(() => {
    return assignments
      .filter(a => moment(a.end).isBefore(moment()))
      .map(a => `Assignment for ${a.resourceId} in ${a.projectId} is overdue`);
  }, [assignments]);

  const generateOptimizationSuggestions = useCallback(() => {
    const suggestions = [];

    const overutilizedResources = resources.filter(r => 
      (calculateWorkDays(r.id) / daysToShow) > 0.8
    );
    
    overutilizedResources.forEach(resource => {
      suggestions.push({
        type: 'reassign',
        description: `Reassign some tasks from ${renderResourceName(resource)} to balance workload`,
        action: () => balanceResourceWorkload(resource.id)
      });
    });

    const underutilizedResources = resources.filter(r => 
      (calculateWorkDays(r.id) / daysToShow) < 0.3
    );

    underutilizedResources.forEach(resource => {
      suggestions.push({
        type: 'assign',
        description: `Assign more tasks to ${renderResourceName(resource)} to improve utilization`,
        action: () => improveResourceUtilization(resource.id)
      });
    });

    return suggestions;
  }, [resources, daysToShow, calculateWorkDays, renderResourceName]);

  const runAISimulation = useCallback(async () => {
    setIsAiLearning(true);
    try {
      const simulationData = {
        resourceUtilization: calculateResourceUtilization(),
        projectProgress: calculateProjectProgress(),
        potentialConflicts: identifyPotentialConflicts(),
        suggestions: generateOptimizationSuggestions(),
      };

      // Send data to backend for AI processing
      const response = await axios.post(`${API_BASE_URL}/ai/simulate`, simulationData);
      
      setSimulationResult(response.data.simulationResult);
      setAiSuggestions(response.data.suggestions);
      setAiModel(response.data.updatedModel);
      
      addNotification("AI Simulation completed. Check the results and suggestions below.");
    } catch (error) {
      console.error('Error running AI simulation:', error);
      addNotification("Error running AI simulation. Please try again.");
    } finally {
      setIsAiLearning(false);
    }
  }, [calculateResourceUtilization, calculateProjectProgress, identifyPotentialConflicts, generateOptimizationSuggestions, addNotification]);

  const observeAndLearn = useCallback(() => {
    const newObservation = {
      timestamp: new Date(),
      resourceCount: resources.length,
      assignmentCount: assignments.length,
      projectCount: projects.length,
      // Add more relevant data points here
    };
    setAiObservations(prev => [...prev, newObservation]);

    // If we have enough observations, trigger learning
    if (aiObservations.length >= 10) {
      learnFromObservations();
    }
  }, [resources, assignments, projects, aiObservations]);

  const learnFromObservations = useCallback(async () => {
    try {
      // Upewnij się, że są dostępne obserwacje przed wykonaniem żądania
      if (aiObservations.length === 0) {
        addNotification("No observations available for AI to learn from.");
        return;
      }
  
      // Pobierz token z localStorage
      const token = localStorage.getItem('access');  // Zmień 'token' na 'access'
  
      if (!token) {
        addNotification("Brak tokenu dostępu. Proszę się zalogować ponownie.");
        return;
      }
  
      // Wyślij żądanie POST do endpointu AI learning
      const response = await axios.post(`${API_BASE_URL}/ai/learn/`, 
        { observations: aiObservations },
        {
          headers: {
            'Authorization': `Bearer ${token}`,  // Użyj poprawnego tokena
            'Content-Type': 'application/json',
          }
        }
      );
  
      // Obsłuż odpowiedź sukcesu
      setAiModel(response.data.updatedModel);
      setAiObservations([]);  // Wyczyść obserwacje po udanym procesie uczenia
      addNotification("AI has learned from recent observations.");
    } catch (error) {
      // Poprawiona obsługa błędów
      console.error('Error in AI learning:', error);
      if (error.response && error.response.status === 401) {
        addNotification("Unauthorized access. Please log in again.");
      } else {
        addNotification("Error in AI learning process. Please try again.");
      }
    }
  }, [aiObservations, addNotification]);


  // ============================
  // 6. AI Action Functions
  // ============================

  const applyAiChanges = useCallback((changes) => {
    changes.forEach(change => {
      switch (change.type) {
        case 'addAssignment':
          setAssignments(prev => [...prev, change.data]);
          break;
        case 'removeAssignment':
          setAssignments(prev => prev.filter(a => a.id !== change.data.id));
          break;
        case 'updateAssignment':
          setAssignments(prev => prev.map(a => a.id === change.data.id ? change.data : a));
          break;
        case 'addResource':
          setResources(prev => [...prev, change.data]);
          break;
        case 'removeResource':
          setResources(prev => prev.filter(r => r.id !== change.data.id));
          break;
        default:
          console.warn('Unknown AI change type:', change.type);
      }
    });
    addNotification("AI changes applied successfully.");
  }, [setAssignments, setResources, addNotification]);

  const balanceResourceWorkload = useCallback((resourceId) => {
    const resourceAssignments = assignments.filter(a => a.resourceId === resourceId);
    const otherResources = resources.filter(r => r.id !== resourceId && r.type === resources.find(r => r.id === resourceId).type);
    
    if (resourceAssignments.length > 0 && otherResources.length > 0) {
      const assignmentToMove = resourceAssignments[resourceAssignments.length - 1];
      const targetResource = otherResources.reduce((prev, curr) => 
        calculateWorkDays(curr.id) < calculateWorkDays(prev.id) ? curr : prev
      );

      setAssignments(prev => prev.map(a => 
        a.id === assignmentToMove.id ? {...a, resourceId: targetResource.id} : a
      ));

      addNotification(`Moved assignment from ${renderResourceName(resources.find(r => r.id === resourceId))} to ${renderResourceName(targetResource)}`);
    }
  }, [assignments, resources, calculateWorkDays, renderResourceName, addNotification]);

  const improveResourceUtilization = useCallback((resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    const availableProjects = projects.filter(p => !assignments.some(a => a.resourceId === resourceId && a.projectId === p.id));
    
    if (availableProjects.length > 0) {
      const newAssignment = {
        id: Date.now(),
        resourceId: resourceId,
        resourceType: resource.type,
        projectId: availableProjects[0].id,
        start: moment().startOf('day').toDate(),
        end: moment().startOf('day').add(5, 'days').toDate(),
      };

      setAssignments(prev => [...prev, newAssignment]);
      addNotification(`Added new assignment for ${renderResourceName(resource)} in project ${availableProjects[0].name}`);
    }
  }, [assignments, resources, projects, renderResourceName, addNotification]);

  // ============================
  // 7. Handlers
  // ============================

  const handleDragEnd = useCallback((result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const reorderedFilteredResources = Array.from(filteredResources);
    const [movedResource] = reorderedFilteredResources.splice(source.index, 1);
    reorderedFilteredResources.splice(destination.index, 0, movedResource);

    const newResourcesOrder = [...reorderedFilteredResources];
    resources.forEach(resource => {
      if (!reorderedFilteredResources.find(r => r.id === resource.id)) {
        newResourcesOrder.push(resource);
      }
    });

    setResources(newResourcesOrder);
    addNotification('Resources reordered successfully.');
  }, [filteredResources, resources, addNotification]);

  const handlePrevious = useCallback(() => {
    setCurrentDate(prev => moment(prev).subtract(daysToShow, 'days'));
  }, [daysToShow]);

  const handleNext = useCallback(() => {
    setCurrentDate(prev => moment(prev).add(daysToShow, 'days'));
  }, [daysToShow]);

  const handleToday = useCallback(() => {
    setCurrentDate(moment().startOf('day'));
  }, []);

  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    setCurrentDate(moment().startOf('week'));
  }, []);

  const handleAssignmentClick = useCallback((assignment) => {
    setSelectedAssignment(assignment);
    setSelectedDate(moment(assignment.start));
  }, []);

  const updateAssignment = useCallback((updatedAssignment) => {
    setAssignments(prev => prev.map(a => a.id === updatedAssignment.id ? updatedAssignment : a));
    setSelectedAssignment(null);
    addNotification(`Assignment updated for ${updatedAssignment.projectId}`);
  }, [addNotification]);

  const deleteAssignment = useCallback((id) => {
    setAssignments(prev => prev.filter(a => a.id !== id));
    setSelectedAssignment(null);
    addNotification('Assignment deleted');
  }, [addNotification]);

  const addNewAssignment = useCallback((resourceId, day) => {
    const resource = resources.find(res => res.id === resourceId);
    if (!resource) {
      console.error('Resource not found.');
      return;
    }
  
    const newAssignment = {
      id: Date.now(),
      resourceId: resource.id,
      resourceType: resource.type,
      projectId: projects[0]?.id || null,
      start: day ? day.toDate() : new Date(),
      end: day ? day.toDate() : new Date(),
    };
  
    setAssignments(prev => [...prev, newAssignment]);
    setSelectedAssignment(newAssignment);
    setSelectedDate(day);
    addNotification(`New assignment added for ${renderResourceName(resource)}`);
  }, [projects, resources, addNotification, renderResourceName]);

  const addNewResource = useCallback((newResource) => {
    const resourceWithId = { ...newResource, id: Date.now() };
    setResources(prev => [...prev, resourceWithId]);
    setShowAddResourceModal(false);
    addNotification(`New resource added: ${newResource.name}`);
  }, [addNotification]);

  const deleteResource = useCallback((resourceId) => {
    setResources(prev => prev.filter(r => r.id !== resourceId));
    setAssignments(prev => prev.filter(a => a.resourceId !== resourceId));
    addNotification('Resource deleted');
  }, [addNotification]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  }, []);

  const handleScroll = useCallback((e) => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = e.target.scrollLeft;
    }
  }, []);

  const handleApplySuggestion = useCallback(async (suggestion) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/ai/apply-suggestion`, { suggestion, currentState: { resources, assignments, projects } });
      applyAiChanges(response.data.changes);
      setAiSuggestions(prev => prev.filter(s => s !== suggestion));
    } catch (error) {
      console.error('Error applying AI suggestion:', error);
      addNotification("Error applying AI suggestion. Please try again.");
    }
  }, [resources, assignments, projects, applyAiChanges, setAiSuggestions, addNotification]);

  // ============================
  // 8. useEffect Hooks
  // ============================

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [machinesResponse, usersResponse, carsResponse, projectsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/machine/`, { headers }),
        axios.get(`${API_BASE_URL}/users/`, { headers }),
        axios.get(`${API_BASE_URL}/cars/`, { headers }),
        axios.get(`${API_BASE_URL}/project/`, { headers }),
      ]);

      const combinedResources = [
        ...usersResponse.data.map(user => ({ ...user, type: 'Person' })),
        ...carsResponse.data.map(car => ({ ...car, type: 'Vehicle' })),
        ...machinesResponse.data.map(machine => ({ ...machine, type: 'Machine' })),
      ];

      setResources(combinedResources);
      setProjects(projectsResponse.data);
      setAssignments([]);
      setLoadingProjects(false);
    } catch (error) {
      console.error('Error fetching data', error);
      setErrorLoadingProjects('Failed to load projects.');
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const filtered = resources.filter(resource => 
      (filter.type === 'All' || resource.type === filter.type) &&
      (filter.project === 'All' || assignments.some(a => a.resourceId === resource.id && a.projectId === filter.project))
    );
    setFilteredResources(filtered);
  }, [resources, assignments, filter]);

  useEffect(() => {
    observeAndLearn();
  }, [resources, assignments, projects, observeAndLearn]);

  // ============================
  // 9. Memoized Calculations
  // ============================

  const assignmentsWithSlots = useMemo(() => {
    const resourceSlotsMap = {};
    filteredResources.forEach(resource => {
      const resourceAssignments = assignments
        .filter(a => a.resourceId === resource.id)
        .sort((a, b) => moment(a.start).diff(moment(b.start)));

      const slots = []; 
      const assignmentSlots = {}; 

      resourceAssignments.forEach(assignment => {
        let placed = false;
        for (let i = 0; i < slots.length; i++) {
          const lastAssignmentInSlot = slots[i][slots[i].length - 1];
          if (moment(lastAssignmentInSlot.end).isBefore(moment(assignment.start))) {
            slots[i].push(assignment);
            assignmentSlots[assignment.id] = i;
            placed = true;
            break;
          }
        }
        if (!placed) {
          slots.push([assignment]);
          assignmentSlots[assignment.id] = slots.length - 1;
        }
      });

      resourceAssignments.forEach(assignment => {
        assignment.slot = assignmentSlots[assignment.id];
      });

      resourceSlotsMap[resource.id] = {
        maxSlots: slots.length,
        assignments: resourceAssignments
      };
    });
    return resourceSlotsMap;
  }, [assignments, filteredResources]);

  const renderDayHeaders = useMemo(() => {
    const days = [];
    for (let i = 0; i < daysToShow; i++) {
      const day = moment(currentDate).add(i, 'days');
      days.push(
        <div
          key={i}
          className="text-center py-2 border-r border-gray-300 font-semibold"
          style={{ flex: `0 0 ${100 / daysToShow}%` }}
        >
          {day.format('ddd DD/MM')}
        </div>
      );
    }
    return days;
  }, [currentDate, daysToShow]);

  const renderAssignments = useCallback((resource) => {
    const resourceSlots = assignmentsWithSlots[resource.id];
    if (!resourceSlots) return null;
  
    const { maxSlots, assignments: resourceAssignments } = resourceSlots;
    const assignmentHeight = maxSlots > 0 ? 100 / maxSlots : 100;
  
    return resourceAssignments
      .filter(assignment => assignment.resourceId === resource.id && assignment.resourceType === resource.type)
      .map(assignment => {
        const startDate = moment(assignment.start).startOf('day');
        const endDate = moment(assignment.end).endOf('day');
        const startOfView = moment(currentDate).startOf('day');
        const endOfView = moment(currentDate).add(daysToShow - 1, 'days').endOf('day');
  
        if (endDate.isBefore(startOfView) || startDate.isAfter(endOfView)) {
          return null;
        }
  
        const left = Math.max(0, startDate.diff(startOfView, 'days'));
        const width = Math.min(daysToShow - left, endDate.diff(startOfView, 'days') - left + 1);
  
        return (
          <div
            key={assignment.id}
            style={{
              position: 'absolute',
              top: `${assignment.slot * assignmentHeight}%`,
              left: `${left * (100 / daysToShow)}%`,
              width: `${width * (100 / daysToShow)}%`,
              height: `${assignmentHeight}%`,
              backgroundColor: projectColors[assignment.projectId] || '#3498db',
              color: 'white',
              padding: '2px',
              overflow: 'hidden',
              cursor: 'pointer',
              fontSize: `${10 / zoomLevel}px`,
              borderRadius: '2px',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              zIndex: 1
            }}
            onClick={() => handleAssignmentClick(assignment)}
            title={`${assignment.projectId}: ${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`}
            className="hover:opacity-90 transition-opacity"
          >
            {assignment.projectId}
          </div>
        );
      });
  }, [assignmentsWithSlots, currentDate, daysToShow, handleAssignmentClick, zoomLevel]);

  const renderResourceRows = useMemo(() => {
    return (
      <Droppable droppableId="resources">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {filteredResources.map((resource, index) => {
              const resourceSlots = assignmentsWithSlots[resource.id];
              const maxSlots = resourceSlots ? resourceSlots.maxSlots : 1;
              const rowHeight = `${30}px`; 

              return (
                <Draggable 
                  key={`resource-${resource.id}-${index}`}
                  draggableId={`draggable-${resource.id}-${index}`}
                  index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="flex border-b border-gray-300"
                      style={{
                        ...provided.draggableProps.style,
                        minHeight: rowHeight,
                        height: rowHeight
                      }}
                      onMouseEnter={() => setHoveredResource(resource)}
                      onMouseLeave={() => setHoveredResource(null)}
                    >
                      <div className="w-64 p-2 bg-gray-100 flex justify-between items-center">
                        <div className="flex items-center">
                          {getResourceIcon(resource.type)}
                          <span className="text-sm">{renderResourceName(resource)}</span> 
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs mr-2">
                            Days: {calculateWorkDays(resource.id)}
                          </span>
                          <Trash2 
                            size={16} 
                            className="text-red-500 cursor-pointer" 
                            onClick={() => deleteResource(resource.id)}
                          />
                        </div>
                      </div>
                      <div className="flex-grow relative" style={{ height: rowHeight }}>
                        {[...Array(daysToShow)].map((_, dayIndex) => (
                          <div
                            key={`day-${resource.id}-${dayIndex}`}
                            className="border-r border-gray-300 absolute top-0 bottom-0"
                            style={{ 
                              left: `${dayIndex * (100 / daysToShow)}%`, 
                              width: `${100 / daysToShow}%` 
                            }}
                            onClick={() => addNewAssignment(resource.id, moment(currentDate).add(dayIndex, 'days'))}
                          />
                        ))}
                        {renderAssignments(resource)}
                      </div>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }, [filteredResources, daysToShow, currentDate, deleteResource, addNewAssignment, renderAssignments, assignmentsWithSlots, calculateWorkDays, getResourceIcon, renderResourceName]);

  const renderSimulationResults = useMemo(() => {
    if (!simulationResult) return null;

    return (
      <div className="mt-8 bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4">AI Simulation Results</h2>
        
        <h3 className="text-xl font-semibold mt-4 mb-2">Resource Utilization</h3>
        <ul>
          {simulationResult.resourceUtilization.map(resource => (
            <li key={resource.id}>
              {resource.name}: {resource.utilization.toFixed(2)}%
            </li>
          ))}
        </ul>

        <h3 className="text-xl font-semibold mt-4 mb-2">Project Progress</h3>
        <ul>
          {simulationResult.projectProgress.map(project => (
            <li key={project.id}>
              {project.name}: {project.progress.toFixed(2)}%
            </li>
          ))}
        </ul>

        <h3 className="text-xl font-semibold mt-4 mb-2">Potential Conflicts</h3>
        <ul>
          {simulationResult.potentialConflicts.map((conflict, index) => (
            <li key={index}>{conflict}</li>
          ))}
        </ul>
      </div>
    );
  }, [simulationResult]);

  // ============================
  // 10. Return JSX
  // ============================

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">AI Resource Planning Assistant</h1>
          
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-2">
                <button onClick={handlePrevious} className="btn btn-blue"><ChevronLeft size={20} /></button>
                <button onClick={handleToday} className="btn btn-blue">Today</button>
                <button onClick={handleNext} className="btn btn-blue"><ChevronRight size={20} /></button>
              </div>
              
              <div className="flex space-x-2">
                {['Week', '2 Weeks', 'Month', '3 Months', '6 Months', 'Year'].map(mode => (
                  <button 
                    key={mode} 
                    onClick={() => changeViewMode(mode)} 
                    className={`btn ${viewMode === mode ? 'btn-blue' : 'btn-gray'}`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <button onClick={handleZoomIn} className="btn btn-blue"><ZoomIn size={20} /></button>
                <button onClick={handleZoomOut} className="btn btn-blue"><ZoomOut size={20} /></button>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Enter new project name"
                className="input"
              />
              
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className="select"
              >
                <option value="All">All Types</option>
                <option value="Person">People</option>
                <option value="Vehicle">Vehicles</option>
                <option value="Machine">Equipment</option>
              </select>
              
              <select
                value={filter.project}
                onChange={(e) => setFilter({ ...filter, project: e.target.value })}
                className="select"
              >
                <option value="All">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              
              <button onClick={() => setShowAddResourceModal(true)} className="btn btn-green flex items-center">
                <Plus size={16} className="mr-1" />
                Add Resource
              </button>
              
              <button 
                onClick={() => setSplitActivityMode(prev => !prev)} 
                className={`btn ${splitActivityMode ? 'btn-green' : 'btn-blue'}`}
              >
                {splitActivityMode ? 'Split Mode On' : 'Split Mode Off'}
              </button>

              <button 
                onClick={runAISimulation} 
                className="btn btn-purple flex items-center"
                disabled={isAiLearning}
              >
                {isAiLearning ? <RefreshCw className="animate-spin mr-2" /> : <Play size={16} className="mr-2" />}
                {isAiLearning ? 'AI Learning...' : 'Run AI Simulation'}
              </button>
            </div>
          </div>
          
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div 
              ref={containerRef}
              className="overflow-x-auto"
              onScroll={handleScroll}
            >
              <div style={{ display: 'flex', minWidth: `${daysToShow * 100 + 256}px` }}>
                <div className="w-64 bg-gray-100 border-r border-gray-300">
                  <div className="p-2 font-bold">Resources</div>
                </div>
                <div className="flex-grow">
                  <div className="flex">
                    {renderDayHeaders}
                  </div>
                </div>
              </div>
              <DragDropContext onDragEnd={handleDragEnd}>
                {renderResourceRows}
              </DragDropContext>
            </div>
          </div>

          {renderSimulationResults}

          {aiSuggestions.length > 0 && (
            <div className="mt-8 bg-white shadow rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">AI Suggestions</h2>
              <ul>
                {aiSuggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-center justify-between mb-2">
                    <span>{suggestion.description}</span>
                    <button 
                      onClick={() => handleApplySuggestion(suggestion)}
                      className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center"
                    >
                      <Check size={16} className="mr-2" />
                      Apply
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </main>

      {selectedAssignment && (
        <EditAssignmentModal
          assignment={selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
          onSave={updateAssignment}
          onDelete={deleteAssignment}
          projects={projects}
        />
      )}

      {showAddResourceModal && (
        <AddResourceModal
          onClose={() => setShowAddResourceModal(false)}
          onAdd={addNewResource}
        />
      )}

      <NotificationContainer 
        notifications={notifications}
        onClose={removeNotification}
        isVisible={isNotificationsVisible}
        onToggle={toggleNotificationsVisibility}
      />

      {hoveredResource && hoveredResource.type === 'Person' && (
        <ResourceInfoPopup resource={hoveredResource} />
      )}
    </div>
  );
};

export default AIAssistant;